export type Maybe<T> = T | null;
export type InputMaybe<T> = Maybe<T>;
/** All built-in and custom scalars, mapped to their actual values */
export type Scalars = {
  ID: { input: string; output: string; }
  String: { input: string; output: string; }
  Boolean: { input: boolean; output: boolean; }
  Int: { input: number; output: number; }
  Float: { input: number; output: number; }
  Binary: { input: string; output: string; }
  Color: { input: string; output: string; }
  Date: { input: string; output: string; }
  DateTime: { input: string; output: string; }
  UUID: { input: string; output: string; }
};

/** What a confirmation did, for the holder of its token; the same values as on the authenticated root. */
export enum ListingApplicationConfirmationOutcome {
  AlreadyApplied = 'ALREADY_APPLIED',
  Confirmed = 'CONFIRMED',
  LoginRequired = 'LOGIN_REQUIRED',
  NotConfirmable = 'NOT_CONFIRMABLE',
  RetryLater = 'RETRY_LATER',
  WrongAccount = 'WRONG_ACCOUNT'
}

/** Only the Easy Entry submission and email-proof consumption are public writes. A submission discloses nothing; a confirmation tells only the token holder what it did. */
export type Mutation = {
  __typename?: 'Mutation';
  /**
   * Spends the token of the confirmation mail, or leaves it valid when the application has to wait for a login.
   * `acknowledged` stays true whatever happened; `outcome` tells the holder of the token, who proved the mailbox, what
   * it did, and never names a Person, an account or an application. A mailbox that belongs to an account answers
   * `LOGIN_REQUIRED` and creates nothing: the application goes on after the login through `claimListingApplication` on
   * the authenticated root. Only a confirmation that creates an application owes the confirmed mailbox one receipt mail,
   * sent later and independently of this response.
   * @external-adr GENESIS-0072-applying-without-an-account-confirms-email-before-a-person-exists
   * @external-adr GENESIS-0082-a-confirmed-application-owes-one-receipt-written-outside-the-confirmation
   */
  confirmListingApplication: PublicListingApplicationAcknowledgement;
  /**
   * Reserves an application and sends its confirmation mail. Also served alone at `POST /public-graphql/applications`,
   * whose body ceiling fits five documents of 10 MiB; this path keeps the global ceiling.
   * @external-adr GENESIS-0084-public-application-bytes-are-journaled-and-cleaned-up-by-a-bounded-pass
   */
  submitListingApplication: PublicListingApplicationAcknowledgement;
};


/** Only the Easy Entry submission and email-proof consumption are public writes. A submission discloses nothing; a confirmation tells only the token holder what it did. */
export type MutationConfirmListingApplicationArgs = {
  token: Scalars['String']['input'];
};


/** Only the Easy Entry submission and email-proof consumption are public writes. A submission discloses nothing; a confirmation tells only the token holder what it did. */
export type MutationSubmitListingApplicationArgs = {
  input: PublicListingApplicationInput;
};

export type PublicColors = {
  __typename?: 'PublicColors';
  background?: Maybe<Scalars['Color']['output']>;
  text?: Maybe<Scalars['Color']['output']>;
};

/** A discovery category of a publicly visible listing: identity and localized name, nothing else. */
export type PublicDiscoveryCategory = {
  __typename?: 'PublicDiscoveryCategory';
  id: Scalars['ID']['output'];
  name?: Maybe<Scalars['String']['output']>;
};

/**
 * A blob of a public listing. Distinct from the authenticated `File` type on purpose: it carries no
 * `fileName`, because the only named blobs are the attachments this root does not serve.
 */
export type PublicFile = {
  __typename?: 'PublicFile';
  content?: Maybe<Scalars['Binary']['output']>;
  fileExtension?: Maybe<Scalars['String']['output']>;
  id: Scalars['UUID']['output'];
  mimeType?: Maybe<Scalars['String']['output']>;
};

/**
 * The content fields and independent categories of a publicly visible listing. The list is the whitelist:
 * adding a field here is the entire cost of publishing it anonymously, which is why it is spelled
 * out in one place a reviewer can read end to end.
 *
 * The field names predate the listing and are kept for their consumers. A listing has no short description
 * and no colour scheme, so `shortDescription`, `lightMode` and `darkMode` are always null. Its main image and
 * further images are content and are served (@external-adr GENESIS-0070-a-listing-is-a-kind-of-project-not-a-shared-item §4).
 */
export type PublicListing = {
  __typename?: 'PublicListing';
  categories: Array<PublicDiscoveryCategory>;
  /** Always null: a listing has no colour scheme. */
  darkMode?: Maybe<PublicColors>;
  /**
   * The closing deadline, the same value as the authenticated `Listing.deadline`; null for a listing without
   * one. A listing whose deadline has passed is no longer served here, so a served deadline lies ahead.
   */
  deadline?: Maybe<Scalars['DateTime']['output']>;
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  /**
   * The further images in the listing's order, scaled to Backdrop's `share_preview` profile (at most 250 pixels high),
   * not the originals; empty for a listing without any. The size is fixed here, not chosen by the caller. Detail only.
   */
  images?: Maybe<Array<PublicFile>>;
  /** Always null: a listing has no colour scheme. */
  lightMode?: Maybe<PublicColors>;
  /**
   * The main image, the original bytes, one image of at most 5 MiB; null for a listing without one. The list serves it
   * in card size only.
   */
  mainImage?: Maybe<PublicFile>;
  name?: Maybe<Scalars['String']['output']>;
  /**
   * The time of the latest publication, the same value the authenticated `Listing.publishedOn` answers with,
   * so the two roots agree about a listing.
   */
  publishedOn?: Maybe<Scalars['DateTime']['output']>;
  /**
   * The reference people quote, `YYYY-NNNN` such as `2026-0042`: assigned by Genesis on creation, unique and never
   * changed. The same value as the authenticated `Listing.reference`, so a listing is named alike on both roots.
   */
  reference: Scalars['String']['output'];
  /**
   * Whom the listing seeks: people, or companies with a person as their contact. It decides the form: a listing that
   * seeks companies takes only `applicationType: COMPANY`, one that seeks people only `PERSON`. Content an applicant needs,
   * like the deadline. A listing from before the search target seeks people.
   * @external-adr GENESIS-0083-a-company-applies-without-an-account-through-its-proven-contact
   */
  searchTarget: PublicListingSearchTarget;
  /** Always null: a listing has no short description. */
  shortDescription?: Maybe<Scalars['String']['output']>;
};

/**
 * `acknowledged` is always true, including ineligible listings, invalid/expired/used tokens and duplicate submissions.
 * `outcome` is null for a submission, which never says anything, and set for a confirmation.
 */
export type PublicListingApplicationAcknowledgement = {
  __typename?: 'PublicListingApplicationAcknowledgement';
  acknowledged: Scalars['Boolean']['output'];
  outcome?: Maybe<ListingApplicationConfirmationOutcome>;
};

export type PublicListingApplicationConsentInput = {
  accepted: Scalars['Boolean']['input'];
  /** Version of the consent/privacy text shown by the client. Required, at most 120 characters. */
  version: Scalars['String']['input'];
};

export type PublicListingApplicationContactInput = {
  apartment?: InputMaybe<Scalars['String']['input']>;
  city?: InputMaybe<Scalars['String']['input']>;
  countryId?: InputMaybe<Scalars['UUID']['input']>;
  email: Scalars['String']['input'];
  familyName?: InputMaybe<Scalars['String']['input']>;
  givenName: Scalars['String']['input'];
  houseNumber?: InputMaybe<Scalars['String']['input']>;
  phone?: InputMaybe<Scalars['String']['input']>;
  street?: InputMaybe<Scalars['String']['input']>;
  website?: InputMaybe<Scalars['String']['input']>;
  zipCode?: InputMaybe<Scalars['String']['input']>;
};

export enum PublicListingApplicationDocumentClassifier {
  CurriculumVitae = 'CURRICULUM_VITAE'
}

/** At most five documents, each nonempty and at most 10 MiB after base64 decoding; a company application's carry no classifier. */
export type PublicListingApplicationDocumentInput = {
  classifier?: InputMaybe<PublicListingApplicationDocumentClassifier>;
  /** Inline base64 bytes. No storage IDs, external URLs or record IDs are accepted. */
  content: Scalars['String']['input'];
  fileName: Scalars['String']['input'];
  title?: InputMaybe<Scalars['String']['input']>;
};

export type PublicListingApplicationInput = {
  /**
   * Who applies: the contact person themself (`PERSON`, also when absent), or a company with the contact as its contact
   * person (`COMPANY`). Must be what the listing's `searchTarget` seeks; anything else is acknowledged and reserves
   * nothing. Chosen explicitly, never inferred from a field.
   * @external-adr GENESIS-0083-a-company-applies-without-an-account-through-its-proven-contact
   */
  applicationType?: InputMaybe<PublicListingApplicationType>;
  /**
   * The applying company's name, required for `COMPANY` and refused for `PERSON`; at most 255 characters. A name is not
   * proof of membership: after the contact confirmed the mailbox, an active company of that name is reused only when the
   * contact's login is already its member, else a new company is created with the contact as its admin. The documents
   * of a company application belong to the company; none of them pre-fills the contact's profile.
   */
  companyName?: InputMaybe<Scalars['String']['input']>;
  consent: PublicListingApplicationConsentInput;
  contact: PublicListingApplicationContactInput;
  documents?: InputMaybe<Array<PublicListingApplicationDocumentInput>>;
  listingId: Scalars['UUID']['input'];
  /**
   * The language of the form the applicant filled in: their explicit choice in the form, else the language the page
   * was shown in. A BCP 47 tag such as de, de-DE, en or en-GB. Any other value, or none, means the instance language; it
   * never refuses the application. Fixed with the reservation: the confirmation mail and later mails about this
   * application use it, and opening the confirmation link in another language changes nothing.
   */
  locale?: InputMaybe<Scalars['String']['input']>;
};

/** Who applies through the public form. */
export enum PublicListingApplicationType {
  Company = 'COMPANY',
  Person = 'PERSON'
}

export type PublicListingConnection = {
  __typename?: 'PublicListingConnection';
  edges: Array<PublicListingEdge>;
  nodes: Array<PublicListingSummary>;
  pageInfo: PublicPageInfo;
};

export type PublicListingEdge = {
  __typename?: 'PublicListingEdge';
  cursor: Scalars['String']['output'];
  node: PublicListingSummary;
};

/**
 * The closed facet whitelist. Equality only, and narrowing only — a facet can never reach a row outside
 * the public set, because it is applied on top of the audience predicate rather than instead of it.
 *
 * There is deliberately no free-text argument, no MATCH-shaped argument and no operator vocabulary
 * (`contains`, `startsWith`, `gt`, …) anywhere on this root: the anonymous surface exposes no search and
 * no matching capability.
 */
export type PublicListingFacetsInput = {
  /** Listings carrying any of these independent categories. */
  categoryIds?: InputMaybe<Array<Scalars['UUID']['input']>>;
  /** Listings published on this calendar day. */
  publishedOn?: InputMaybe<Scalars['Date']['input']>;
};

/** Whom a public listing seeks. */
export enum PublicListingSearchTarget {
  Companies = 'COMPANIES',
  People = 'PEOPLE'
}

/**
 * A publicly visible listing as a page of `publicListings` shows it: a strict subset of `PublicListing`, the fields
 * a card needs. The main image is fixed to Backdrop's `card` profile, chosen here and not by the caller, and the
 * further images are absent, so a page costs at most one small rendition per listing. The fields `PublicListing`
 * keeps only for older consumers and that are always null for a listing (`shortDescription`, `lightMode`,
 * `darkMode`) are not repeated on this type.
 */
export type PublicListingSummary = {
  __typename?: 'PublicListingSummary';
  categories: Array<PublicDiscoveryCategory>;
  /** The same value as `PublicListing.deadline`. */
  deadline?: Maybe<Scalars['DateTime']['output']>;
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  /** The main image scaled to Backdrop's `card` profile, not the original; null for a listing without one. */
  mainImage?: Maybe<PublicFile>;
  name?: Maybe<Scalars['String']['output']>;
  /** The same value as `PublicListing.publishedOn`. */
  publishedOn?: Maybe<Scalars['DateTime']['output']>;
  /** The same value as `PublicListing.reference`. */
  reference: Scalars['String']['output'];
  /** The same value as `PublicListing.searchTarget`. */
  searchTarget: PublicListingSearchTarget;
};

export type PublicPageInfo = {
  __typename?: 'PublicPageInfo';
  /** The cursor of the current page's last item. To be used when fetching the next page. */
  endCursor?: Maybe<Scalars['String']['output']>;
  /** True if there is at least one more page available, otherwise false */
  hasNextPage: Scalars['Boolean']['output'];
  /** True if there is a previous page available, otherwise false */
  hasPreviousPage: Scalars['Boolean']['output'];
  /** The cursor of the current page's first item. To be used when fetching the previous page. */
  startCursor?: Maybe<Scalars['String']['output']>;
};

export type Query = {
  __typename?: 'Query';
  /**
   * One public listing by id, for a shared deep-link. Null both when no such row exists and when the row
   * exists but is not public: the two cases are indistinguishable to the caller on purpose, so this is not
   * an oracle for "does this id exist".
   */
  publicListing?: Maybe<PublicListing>;
  /**
   * Public listings, newest first, cursor-paged. Returns exactly the listings that are open (their deadline
   * not passed) and carry a recipient the anonymous principal reads through, which is the `is_public` set and
   * only ever that. Shared items are no longer served here.
   *
   * Each node is a card (`PublicListingSummary`): the main image in card size and no further images. The full
   * listing with its further images is `publicListing`.
   *
   * `first` defaults to 20 and is capped at 100; a larger value is clamped, not rejected.
   */
  publicListings: PublicListingConnection;
  /** Whether anonymous Share access is enabled for this installation; exposes no content or permissions. */
  publicShareEnabled: Scalars['Boolean']['output'];
};


export type QueryPublicListingArgs = {
  id: Scalars['UUID']['input'];
};


export type QueryPublicListingsArgs = {
  after?: InputMaybe<Scalars['String']['input']>;
  facets?: InputMaybe<PublicListingFacetsInput>;
  first?: InputMaybe<Scalars['Int']['input']>;
};
